import type { WebPushSubscription } from "@luckymenu/core";
import type { PushAdapter, PushPermission } from "@luckymenu/staff-core";

/** The VAPID public key (URL-safe base64) as the bytes `pushManager.subscribe` wants. */
export function keyBytes(base64Url: string): Uint8Array<ArrayBuffer> {
	const base64 = (base64Url + "=".repeat((4 - (base64Url.length % 4)) % 4))
		.replace(/-/g, "+")
		.replace(/_/g, "/");
	const raw = atob(base64);
	const bytes = new Uint8Array(new ArrayBuffer(raw.length));
	for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
	return bytes;
}

function sameKey(a: ArrayBuffer | null | undefined, b: Uint8Array): boolean {
	if (!a) return false;
	const x = new Uint8Array(a);
	return x.length === b.length && x.every((v, i) => v === b[i]);
}

function toSubscription(sub: PushSubscription): WebPushSubscription | null {
	const json = sub.toJSON();
	if (!json.endpoint || !json.keys?.p256dh || !json.keys.auth) return null;
	return { endpoint: json.endpoint, keys: { p256dh: json.keys.p256dh, auth: json.keys.auth } };
}

/**
 * Web Push on staff web (P3-T32) through the PWA's service worker, which
 * shows the notifications (`public/push-sw.js`). Browsers ask for permission
 * only from a click; staff-core decides when to offer (`usePush`).
 */
export function webPush(target: Window = window): PushAdapter {
	const Notice = (target as unknown as { Notification?: typeof Notification }).Notification;
	const supported =
		Boolean(Notice) && "serviceWorker" in target.navigator && "PushManager" in target;
	const registration = () => target.navigator.serviceWorker.ready;
	return {
		permission: (): PushPermission =>
			Notice && supported ? (Notice.permission as PushPermission) : "unsupported",
		async subscribe(publicKey) {
			if (!Notice || !supported) return null;
			const permission =
				Notice.permission === "default" ? await Notice.requestPermission() : Notice.permission;
			if (permission !== "granted") return null;
			const manager = (await registration()).pushManager;
			const key = keyBytes(publicKey);
			let existing = await manager.getSubscription();
			// Keys were changed on the server: subscribe again with the new one.
			if (existing && !sameKey(existing.options.applicationServerKey, key)) {
				await existing.unsubscribe();
				existing = null;
			}
			const sub =
				existing ?? (await manager.subscribe({ userVisibleOnly: true, applicationServerKey: key }));
			return toSubscription(sub);
		},
		async current() {
			if (!Notice || !supported || Notice.permission !== "granted") return null;
			const sub = await (await registration()).pushManager.getSubscription();
			return sub ? toSubscription(sub) : null;
		},
		async unsubscribe() {
			if (!supported) return;
			const sub = await (await registration()).pushManager.getSubscription();
			await sub?.unsubscribe();
		},
	};
}
